// packages/utils/wallet.js

import { BrowserProvider } from "ethers";

/**
 * Check if MetaMask is installed
 */
export const hasMetaMask = () =>
  typeof window !== "undefined" && typeof window.ethereum !== "undefined";

/**
 * Get Ethers.js provider instance
 */
export const getProvider = () => {
  if (!hasMetaMask()) throw new Error("MetaMask not found");
  return new BrowserProvider(window.ethereum);
};

/**
 * Request wallet accounts from MetaMask.
 * - If locked → triggers popup
 * - If disconnected → triggers permission prompt
 * - If authorized → returns silently
 */
export const requestAccounts = async () => {
  if (!hasMetaMask()) throw new Error("MetaMask not detected");

  try {
    // Ask MetaMask to prompt if locked or missing permission
    await window.ethereum.request({
      method: "wallet_requestPermissions",
      params: [{ eth_accounts: {} }],
    });
  } catch (permErr) {
    // User might have rejected, or permission already granted — ignore
    if (permErr.code !== 4001) {
      console.warn("wallet_requestPermissions:", permErr.message);
    }
  }

  // Always call eth_requestAccounts next (this triggers popup if locked)
  const accounts = await window.ethereum.request({
    method: "eth_requestAccounts",
  });

  if (!accounts || accounts.length === 0)
    throw new Error("No accounts returned. Please unlock MetaMask.");

  return accounts;
};

/**
 * Shorten address for UI
 */
export const shortAddress = (addr) =>
  addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : "";

/**
 * Subscribe to MetaMask events
 */
export const onMetaMaskEvents = (onAccountsChanged, onChainChanged) => {
  if (!hasMetaMask()) return () => {};
  const { ethereum } = window;
  if (onAccountsChanged) ethereum.on("accountsChanged", onAccountsChanged);
  if (onChainChanged) ethereum.on("chainChanged", onChainChanged);

  return () => {
    if (onAccountsChanged)
      ethereum.removeListener("accountsChanged", onAccountsChanged);
    if (onChainChanged)
      ethereum.removeListener("chainChanged", onChainChanged);
  };
};

/**
 * Ensure correct network (Polygon Amoy by default)
 */
export const ensureChain = async (targetChainId = "0x13882") => {
  const { ethereum } = window;
  if (!ethereum) throw new Error("MetaMask not found");

  const currentChainId = await ethereum.request({ method: "eth_chainId" });
  if (currentChainId.toLowerCase() === targetChainId.toLowerCase()) return true;

  try {
    await ethereum.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: targetChainId }],
    });
    console.log("✅ Switched to Polygon Amoy Testnet");
    return true;
  } catch (switchError) {
    if (switchError.code === 4902) {
      try {
        await ethereum.request({
          method: "wallet_addEthereumChain",
          params: [
            {
              chainId: "0x13882",
              chainName: "Polygon Amoy Testnet",
              nativeCurrency: {
                name: "MATIC",
                symbol: "MATIC",
                decimals: 18,
              },
              rpcUrls: ["https://rpc-amoy.polygon.technology/"],
              blockExplorerUrls: ["https://amoy.polygonscan.com/"],
            },
          ],
        });
        console.log("✅ Polygon Amoy Testnet added successfully");
        return true;
      } catch (addError) {
        console.error("⚠️ Failed to add Amoy network:", addError);
        throw new Error(
          "Failed to add Polygon Amoy Testnet. Please add it manually in MetaMask."
        );
      }
    } else {
      console.error("⚠️ User rejected or switch failed:", switchError);
      throw new Error(
        "Please switch MetaMask network to Polygon Amoy (0x13882) and retry."
      );
    }
  }
};